import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatToShortMonth } from "@/lib/time_utils";
import { IconButton } from "@/components/ui/IconButton";
import { Button } from "@/components/ui/Button";
import {
  Body,
  H4,
  FontColor,
  TextSize,
  FontWeight,
} from "@/components/ui/Typography";

type YM = { year: number; month: number };
type Range = { from: YM; to: YM };

const MAX_MONTHS = 12;

const ymKey = (ym: YM) => ym.year * 100 + ym.month;
const compareYM = (a: YM, b: YM) => ymKey(a) - ymKey(b);
const clampFromTo = (a: YM, b: YM): Range =>
  compareYM(a, b) <= 0 ? { from: a, to: b } : { from: b, to: a };

function addMonths(ym: YM, delta: number): YM {
  const d = new Date(ym.year, ym.month - 1 + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}
function monthsDiffInclusive(a: YM, b: YM) {
  const r = clampFromTo(a, b);
  return (r.to.year - r.from.year) * 12 + (r.to.month - r.from.month) + 1;
}
function clampToMax12(from: YM, to: YM): Range {
  const r = clampFromTo(from, to);
  const len = monthsDiffInclusive(r.from, r.to);
  if (len <= MAX_MONTHS) return r;
  return { from: addMonths(r.to, -(MAX_MONTHS - 1)), to: r.to };
}
function clampToMax12WithBias(from: YM, to: YM, bias: "from" | "to"): Range {
  const r = clampFromTo(from, to);
  const len = monthsDiffInclusive(r.from, r.to);
  if (len <= MAX_MONTHS) return r;
  return bias === "to"
    ? { from: addMonths(r.to, -(MAX_MONTHS - 1)), to: r.to }
    : { from: r.from, to: addMonths(r.from, MAX_MONTHS - 1) };
}

export default function RangeMonthYearSelector({
  value,
  onChange,
  labelFrom,
  labelTo,
}: {
  value: Range;
  onChange: (v: Range) => void;
  labelFrom?: string;
  labelTo?: string;
}) {
  const { t, i18n } = useTranslation("common");
  const locale = i18n.language;

  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        formatToShortMonth(
          new Intl.DateTimeFormat(locale, { month: "short" }).format(
            new Date(2000, i, 1)
          )
        )
      ),
    [locale]
  );

  const [open, setOpen] = useState(false);
  const [temp, setTemp] = useState<Range>(value);
  const [selecting, setSelecting] = useState<"from" | "to" | null>(null);

  const [leftYear, setLeftYear] = useState<number>(value.to.year - 1);
  const [rightYear, setRightYear] = useState<number>(value.to.year);

  useEffect(() => setTemp(value), [value]);

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        onChange(clampToMax12(temp.from, temp.to));
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [temp, onChange]);

  const caption = `${months[temp.from.month - 1]} ${temp.from.year} - ${
    months[temp.to.month - 1]
  } ${temp.to.year}`;

  const handlePick = (year: number, monthIdx: number, side: "from" | "to") => {
    const picked: YM = { year, month: monthIdx + 1 };

    if (!selecting) {
      setSelecting(side === "from" ? "to" : "from");
      setTemp({ from: picked, to: picked });
      return;
    }

    const next = clampToMax12WithBias(temp.from, picked, side);
    setTemp(next);
    setSelecting(null);
  };

  const isInRange = (y: number, mIdx: number) => {
    const ym: YM = { year: y, month: mIdx + 1 };
    const k = ymKey(ym);
    const a = ymKey(temp.from);
    const b = ymKey(temp.to);
    return k >= Math.min(a, b) && k <= Math.max(a, b);
  };

  const isEdge = (y: number, mIdx: number) => {
    const k = y * 100 + (mIdx + 1);
    return k === ymKey(temp.from) || k === ymKey(temp.to);
  };

  const YearColumn = ({
    year,
    setYear,
    side,
  }: {
    year: number;
    setYear: (y: number) => void;
    side: "from" | "to";
  }) => (
    <div className="rounded-xl border border-gray-100 p-2">
      <div className="flex items-center justify-between mb-2">
        <IconButton
          aria-label="prev-year"
          onClick={() => setYear(year - 1)}
          className="rounded-full"
        >
          <ChevronLeft className="h-5 w-5" />
        </IconButton>
        <H4 className="text-gray-800">{year}</H4>
        <IconButton
          aria-label="next-year"
          onClick={() => setYear(year + 1)}
          className="rounded-full"
        >
          <ChevronRight className="h-5 w-5" />
        </IconButton>
      </div>

      <div className="grid grid-cols-4 gap-1">
        {months.map((m, i) => {
          const inRange = isInRange(year, i);
          const edge = isEdge(year, i);
          const base =
            "text-xs px-3 py-2 rounded-lg transition-colors text-center";
          const active = edge
            ? "bg-gray-900 text-white"
            : inRange
            ? "bg-gray-100 text-gray-800"
            : "bg-white text-gray-800 hover:bg-gray-50";
          return (
            <button
              key={`${year}-${i}`}
              onClick={() => handlePick(year, i, side)}
              className={`${base} ${active}`}
            >
              {m}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        className="inline-flex items-center bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 hover:text-black px-2 py-1.5"
        onClick={() => {
          setOpen((o) => !o);
          setSelecting(null);
          setLeftYear(temp.to.year - 1);
          setRightYear(temp.to.year);
        }}
      >
        <CalendarDays className="w-4 h-4 mr-2" />
        {caption}
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-2 w-[560px] bg-white border border-gray-200 rounded-xl shadow-xl p-3">
          <div className="text-center mb-3">
            <Body
              as="div"
              size={TextSize.Large}
              weight={FontWeight.Medium}
              color={FontColor.Gray600}
              className="select-none"
            >
              {caption}
            </Body>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <YearColumn year={leftYear} setYear={setLeftYear} side="from" />
            <YearColumn year={rightYear} setYear={setRightYear} side="to" />
          </div>

          <div className="flex items-center justify-between mt-3">
            <Body color={FontColor.Gray500} size={TextSize.Small}>
              {" "}
            </Body>
            <div className="flex gap-2">
              <Button
                variant="text"
                onClick={() => {
                  const now = new Date();
                  const reset: Range = {
                    from: { year: now.getFullYear(), month: 1 },
                    to: { year: now.getFullYear(), month: now.getMonth() + 1 },
                  };
                  setTemp(reset);
                  onChange(reset);
                  setOpen(false);
                }}
              >
                {t("clear")}
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  onChange(clampToMax12(temp.from, temp.to));
                  setOpen(false);
                }}
              >
                {t("confirm")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
